import { isNumber } from 'lodash';
import { transferXYToAngle, translateAngleToXY } from '@/utils/math-kit';
import { extractBaseTextInfoFromVectorLayer, TextMeasurer } from '@/utils/text-kit';
import { transformFoundationVectorLayerDataToClient } from '@/utils/transform-kit';
import {
    MIN_IMG_SIZE,
    StretchOptionType,
    TextWritingType,
    VectorType,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import { calculateRealDistance, calculateVirtualDistance } from '../utils';

type Behaviors = Pick<
    ILayerBehavior,
    | 'onBeforeResizeUpdate'
    | 'onBeforeScaleUpdate'
    | 'getActiveBaseInfo'
    | 'getScaleMinRatio'
    | 'getLayerMinSize'
    | 'getScaleIsNeedLimitSize'
    | 'whenGroupCanResize'
    | 'onBeforeGetLayerWithClone'
>;

export const createOperationBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        onBeforeResizeUpdate(layer, { originInfo, isChangeHeight }) {
            const layerData = layer.data as IVectorLayerProps;
            const { vectorType } = layer.data as IVectorLayerProps;

            // 防止矢量图层没有设置 proactiveWidth、proactiveHeight
            if (!layer.__proactiveWidth) layer.__proactiveWidth = layer.width;
            if (!layer.__proactiveHeight) layer.__proactiveHeight = layer.height;

            // 矩形圆角变化
            if (vectorType === VectorType.Rect) {
                // 矩形，当圆角触发边界问题
                const halfSize = Math.round(Math.min(layer.width, layer.height) / 2);
                layerData.radius = originInfo.radius > halfSize ? halfSize : originInfo.radius;

                const { borderRadius } = layerData;
                if (borderRadius && originInfo.borderRadius) {
                    const { leftBottom, rightBottom, rightTop, leftTop } = originInfo.borderRadius;
                    layerData.borderRadius = {
                        leftBottom: leftBottom > halfSize ? halfSize : leftBottom,
                        rightBottom: rightBottom > halfSize ? halfSize : rightBottom,
                        rightTop: rightTop > halfSize ? halfSize : rightTop,
                        leftTop: leftTop > halfSize ? halfSize : leftTop,
                    };
                }
            }

            // 智能模式 每次用原始图片和当前图层大小算出比例
            const { stretchOption } = layerData;
            if (stretchOption === StretchOptionType.cover) {
                const { naturalWidth, naturalHeight, imageTransform } = layerData;
                const newWidthRatio = naturalWidth / layer.width;
                const newHeightRatio = naturalHeight / layer.height;

                const ratio = newWidthRatio < newHeightRatio ? newWidthRatio : newHeightRatio;

                imageTransform.widthRatio = naturalWidth / ratio / layer.width;
                imageTransform.heightRatio = naturalHeight / ratio / layer.height;
            }

            // 若矢量图层包含文本信息，则 resize 行为需要符合文本图层
            const { textInfo } = layerData;
            if (textInfo && textInfo.text) {
                const baseTextInfo = extractBaseTextInfoFromVectorLayer(layer);
                const { writingMode, fontSize, listStyle, lineSpacing } = baseTextInfo;
                const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

                const textSize = TextMeasurer.measure(layer, {
                    keepConstantWidth: isHorizontal,
                    keepConstantHeight: !isHorizontal,
                    cancelMinSize: true,
                    preservePadding: textInfo?.padding,
                });

                /**
                 * 文字为水平排版：
                 *
                 *  - 改变高度时：
                 *      - 宽度保持不变
                 *      - 高度 `>=` 文字内容高度
                 *  - 改变宽度时：
                 *      - 宽度 `>=` 文字最小宽度
                 *      - 高度跟随文字内容高度
                 *
                 * 文字为垂直排版:
                 *
                 *  - 改变宽度时:
                 *      - 宽度 `>=` 文字内容宽度
                 *  - 改变高度时:
                 *      - 宽度 `>=` 文字最小宽度
                 */
                if (isHorizontal) {
                    if (isChangeHeight) {
                        const minHeight = textSize.height;
                        layer.height = layer.height <= minHeight ? minHeight : layer.height;
                    } else {
                        const minWidth =
                            (listStyle ? 3 * fontSize : fontSize) +
                            (textInfo?.padding?.left || 0) +
                            (textInfo?.padding?.right || 0);
                        layer.width = layer.width <= minWidth ? minWidth : layer.width;
                        layer.height =
                            textSize.height <= layer.__proactiveHeight ? layer.__proactiveHeight : textSize.height;
                    }
                } else {
                    if (isChangeHeight) {
                        const minHeight =
                            fontSize * lineSpacing * (listStyle ? 3 : 1) +
                            (textInfo?.padding?.top || 0) +
                            (textInfo?.padding?.bottom || 0);
                        layer.width =
                            textSize.width <= layer.__proactiveWidth ? layer.__proactiveWidth : textSize.width;
                        layer.height = layer.height <= minHeight ? minHeight : layer.height;
                    } else {
                        const minWidth = textSize.width;
                        layer.width = layer.width <= minWidth ? minWidth : layer.width;
                    }
                }
            }

            // 更新 proactive size
            if (isChangeHeight) {
                layer.__proactiveHeight = layer.height;
            } else {
                layer.__proactiveWidth = layer.width;
            }
        },

        onBeforeScaleUpdate(layer, info) {
            // prevLayerInfo 为开始缩放时部分图层信息
            // originInfo 这里一定有值
            const prevLayerInfo = info.originInfo as NonNullable<ILayerOriginInfo>;
            const layerData = layer.data as IVectorLayerProps;

            const originWidth = prevLayerInfo.width;
            const ratio = layer.width / originWidth;

            const radius = prevLayerInfo.radius;
            if (isNumber(layerData.radius) && isNumber(radius)) {
                const nextRadius = Math.floor(radius * ratio);
                layerData.radius = nextRadius;
            }

            if (prevLayerInfo.borderRadius) {
                const { leftBottom, rightBottom, rightTop, leftTop } = prevLayerInfo.borderRadius;
                layerData.borderRadius = {
                    leftBottom: Math.floor(leftBottom * ratio),
                    rightBottom: Math.floor(rightBottom * ratio),
                    rightTop: Math.floor(rightTop * ratio),
                    leftTop: Math.floor(leftTop * ratio),
                };
            }

            const nextStrokeWidth = prevLayerInfo.strokeWidth * ratio;
            layerData.strokeWidth = nextStrokeWidth >= 1 ? Math.round(nextStrokeWidth) : nextStrokeWidth; // 防止小于 1 时，strokeWidth 变成 0

            //阴影跟随图层位置
            if (layerData.vectorType !== VectorType.Graph) {
                const needUpdateShadow = layerData.effect.shadow?.[0] && layer.width;
                if (needUpdateShadow) {
                    const { x, y } = layerData.effect.shadow[0];
                    const angle = transferXYToAngle(x, y);
                    //原来的距离
                    const distance = calculateVirtualDistance(x, y, layer.width);
                    // 基于原来的距离 算出图层缩放后的距离
                    const _distance = calculateRealDistance(distance, layer.width);
                    const { x: x1, y: y1 } = translateAngleToXY(angle, _distance);
                    layerData.effect.shadow[0].x = x1;
                    layerData.effect.shadow[0].y = y1;
                }
            }

            // TODO: 此处 hack 逻辑过多，代码杂乱，需要整理
            // 根据文字内容重新调整图层尺寸
            const { textInfo } = layerData;
            if (textInfo && textInfo.text) {
                const isInit = layer.__runtime?.isInit;

                // HACK: 有可能在数据没有转换为客户端数据前，就触发了缩放（比如：从「我的空间」添加素材到舞台）
                // [bugfix]: https://jira.meitu.com/browse/MTXXPRO-64825
                // [bugfix]: https://jira.meitu.com/browse/MTXXPRO-64806
                let targetLayer: ILayerProps; // 用于计算文本内容尺寸

                if (isInit) {
                    // 若是智能缩放（调整舞台尺寸），此时需要走等比逻辑，即需要对文本信息进行缩放
                    if (info.isFromAdjustScale) {
                        const layerData = layer.data as IVectorLayerProps;
                        let textInfo = layerData.textInfo as VectorTextInfo;

                        textInfo = {
                            ...textInfo,
                            fontSize: textInfo.fontSize * ratio,
                            letterSpacing: textInfo.letterSpacing * ratio,
                        };

                        if (textInfo.padding) {
                            textInfo.padding = {
                                left: textInfo.padding.left * ratio,
                                right: textInfo.padding.right * ratio,
                                top: textInfo.padding.top * ratio,
                                bottom: textInfo.padding.bottom * ratio,
                            };
                        }

                        layerData.textInfo = textInfo;
                        layer.data = layerData;
                    }

                    targetLayer = layer;
                } else {
                    // 图层未初始化前走等比缩放
                    const foundationLayerData = layer.data as IVectorLayerProps;
                    let textInfo = foundationLayerData.textInfo as VectorTextInfo;
                    textInfo = {
                        ...textInfo,
                        fontSize: textInfo.fontSize * ratio,
                        letterSpacing: textInfo.letterSpacing * ratio,
                    };

                    if (textInfo.padding) {
                        textInfo.padding = {
                            left: textInfo.padding.left * ratio,
                            right: textInfo.padding.right * ratio,
                            top: textInfo.padding.top * ratio,
                            bottom: textInfo.padding.bottom * ratio,
                        };
                    }

                    foundationLayerData.textInfo = textInfo;
                    layer.data = foundationLayerData;

                    const clientLayerData = transformFoundationVectorLayerDataToClient(foundationLayerData);
                    targetLayer = { ...layer, data: clientLayerData };
                }

                const { fontSize, listStyle, lineSpacing, writingMode } = (targetLayer.data as IVectorLayerProps)
                    .textInfo as VectorTextInfo;
                const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

                const textSize = TextMeasurer.measure(targetLayer, {
                    keepConstantWidth: isHorizontal,
                    keepConstantHeight: !isHorizontal,
                    cancelMinSize: true,
                    preservePadding: textInfo?.padding,
                });

                if (isHorizontal) {
                    const minWidth =
                        (listStyle ? 3 * fontSize : fontSize) +
                        (textInfo?.padding?.left || 0) +
                        (textInfo?.padding?.right || 0);

                    layer.width = layer.width <= minWidth ? minWidth : layer.width;
                    layer.height = Math.max(textSize.height, layer.height);
                } else {
                    const minHeight =
                        fontSize * lineSpacing * (listStyle ? 3 : 1) +
                        (textInfo?.padding?.top || 0) +
                        (textInfo?.padding?.bottom || 0);

                    layer.width = Math.max(textSize.width, layer.width);
                    layer.height = layer.height <= minHeight ? minHeight : layer.height;
                }
            }

            layer.__proactiveWidth = layer.width;
            layer.__proactiveHeight = layer.height;
        },

        getActiveBaseInfo(layer) {
            const layerData = layer.data as IVectorLayerProps;
            const { naturalHeight, naturalWidth, strokeWidth, radius, vectorType, borderRadius } = layerData;

            const cutWidth = Math.min(naturalWidth, MIN_IMG_SIZE);
            const cutHeight = Math.min(naturalHeight, MIN_IMG_SIZE);

            return {
                cutWidth,
                cutHeight,
                strokeWidth,
                radius,
                vectorType,
                borderRadius,
            };
        },

        getScaleMinRatio(originInfo) {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        whenGroupCanResize() {
            const horizontal = true;
            const vertical = true;
            return { horizontal, vertical };
        },

        onBeforeGetLayerWithClone(layer) {
            delete layer.__isOnTextEditing;
        },
    };

    return behaviors;
};
